// ─── Element Style Panel ──────────────────────────────────────
// Per-element styling controls for text, images, shapes, and ornaments.
// Text: font, size, color, alignment, letter-spacing, line-height, text-transform
// Image: width slider, border radius, opacity
// Shape: fill, stroke, stroke width, opacity, border radius
// Ornament: color, opacity

import React, { useState, useRef, useCallback } from 'react';
import { Type, Palette, Maximize2, Image as ImageIcon, AlignLeft, AlignCenter, AlignRight, CaseSensitive, Upload, Music2, X, Loader2 } from 'lucide-react';
import axios from 'axios';
import RsvpButtonStylePanel from './RsvpButtonStylePanel';

const API = process.env.REACT_APP_BACKEND_URL;

const FONT_OPTIONS = [
  { id: 'serif', label: 'Serif', value: '"Playfair Display", Georgia, serif' },
  { id: 'sans', label: 'Sans', value: '"Inter", system-ui, sans-serif' },
  { id: 'elegant', label: 'Elegant', value: '"Cormorant Garamond", Georgia, serif' },
  { id: 'modern', label: 'Modern', value: '"DM Serif Display", Georgia, serif' },
  { id: 'handwritten', label: 'Hand', value: '"Caveat", cursive' },
  { id: 'typewriter', label: 'Mono', value: '"Special Elite", "Courier New", monospace' },
  { id: 'manrope', label: 'Clean', value: '"Manrope", system-ui, sans-serif' },
];

const COLOR_PRESETS = [
  '#000000', '#1a1a1a', '#333333', '#666666', '#999999',
  '#ffffff', '#f5f0eb', '#c8a96b', '#d4af37', '#C88F8F',
  '#556B5D', '#9b7f6b', '#2A2220', '#1C241F',
];

const BG_COLOR_PRESETS = [
  '#ffffff', '#fdfbf8', '#f5f0eb', '#f0ebe3', '#e8e0d5',
  '#1a1a1a', '#2A2220', '#1C241F', '#d4af37', '#556B5D',
];

const TEXT_TRANSFORMS = [
  { value: 'none', label: 'Aa' },
  { value: 'uppercase', label: 'AA' },
  { value: 'capitalize', label: 'Ab' },
  { value: 'lowercase', label: 'aa' },
];

export default function ElementStylePanel({ element, overrides, onUpdate, onResize, sectionId, eventId, slug, previewToken }) {
  if (!element) return null;

  const isText = ['text_block', 'text_note', 'title_block'].includes(element.type);
  const isImage = element.type === 'image_card';
  const isShape = element.type === 'shape';
  const isOrnament = element.type === 'ornament';
  const isVinyl = element.type === 'vinyl_player';
  const isSlideshow = element.type === 'slideshow_element';
  const isRsvpButton = element.type === 'rsvp_button';

  if (!isText && !isImage && !isShape && !isOrnament && !isVinyl && !isSlideshow && !isRsvpButton) return null;

  // ─── RSVP Button has its own dedicated panel ──────────────────
  if (isRsvpButton) {
    return (
      <>
        <RsvpButtonStylePanel
          element={element}
          overrides={overrides}
          onUpdate={onUpdate}
          sectionId={sectionId}
          eventId={eventId}
          slug={slug}
          previewToken={previewToken}
        />
        <EntrancePicker element={element} overrides={overrides} onUpdate={onUpdate} sectionId={sectionId} />
      </>
    );
  }

  // ─── Vinyl Player has its own dedicated panel ─────────────────
  if (isVinyl) {
    return (
      <>
        <VinylStylePanel element={element} overrides={overrides} onUpdate={onUpdate} sectionId={sectionId} eventId={eventId} />
        <EntrancePicker element={element} overrides={overrides} onUpdate={onUpdate} sectionId={sectionId} />
      </>
    );
  }

  // ─── Slideshow Element panel ──────────────────────────────────
  if (isSlideshow) {
    return (
      <>
        <SlideshowStylePanel element={element} overrides={overrides} onUpdate={onUpdate} sectionId={sectionId} eventId={eventId} />
        <EntrancePicker element={element} overrides={overrides} onUpdate={onUpdate} sectionId={sectionId} />
      </>
    );
  }

  const currentStyle = overrides?.customStyle || {};

  const handleStyleChange = (key, value) => {
    onUpdate(sectionId, element.id, {
      customStyle: { ...currentStyle, [key]: value },
    });
  };

  // ── Text Style Panel ──
  if (isText) {
    const fontSize = parseInt(currentStyle.fontSize) || 16;
    const letterSpacing = parseFloat(currentStyle.letterSpacing) || 0;
    const lineHeight = parseFloat(currentStyle.lineHeight) || 1.5;
    const textTransform = currentStyle.textTransform || 'none';

    const textPanel = (
      <div
        className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden"
        data-testid="element-style-panel"
      >
        <div className="px-3 py-2.5 border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50">
          <h3 className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
            <Type className="w-3 h-3" /> Text Style
          </h3>
        </div>

        <div className="p-3 space-y-3">
          {/* Font Family */}
          <div>
            <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1 block">Font</label>
            <div className="grid grid-cols-4 gap-1">
              {FONT_OPTIONS.map(f => (
                <button
                  key={f.id}
                  onClick={() => handleStyleChange('fontFamily', f.value)}
                  className={`px-1.5 py-1 rounded text-[10px] font-medium transition-all ${
                    currentStyle.fontFamily === f.value
                      ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 ring-1 ring-blue-300'
                      : 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-100'
                  }`}
                  style={{ fontFamily: f.value }}
                  data-testid={`font-${f.id}`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {/* Font Size — slider + numeric input */}
          <div>
            <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5 flex items-center gap-1">
              <Maximize2 className="w-2.5 h-2.5" /> Size
            </label>
            <div className="flex items-center gap-2.5">
              <input
                type="range"
                min={8}
                max={96}
                step={1}
                value={fontSize}
                onChange={e => handleStyleChange('fontSize', `${e.target.value}px`)}
                className="flex-1 accent-blue-500 h-1.5"
                data-testid="font-size-slider"
              />
              <div className="flex items-center gap-1 flex-shrink-0">
                <input
                  type="number"
                  min={8}
                  max={96}
                  value={fontSize}
                  onChange={e => {
                    const v = Math.min(96, Math.max(8, parseInt(e.target.value) || 16));
                    handleStyleChange('fontSize', `${v}px`);
                  }}
                  className="w-12 px-1.5 py-0.5 text-center text-xs bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded text-gray-700 dark:text-gray-300"
                  data-testid="font-size-input"
                />
                <span className="text-[10px] text-gray-400">px</span>
              </div>
            </div>
          </div>

          {/* Letter Spacing */}
          <div>
            <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1 flex items-center justify-between">
              <span>Letter Spacing</span>
              <span className="text-gray-400">{letterSpacing.toFixed(1)}px</span>
            </label>
            <input
              type="range"
              min={-2}
              max={20}
              step={0.5}
              value={letterSpacing}
              onChange={e => handleStyleChange('letterSpacing', parseFloat(e.target.value))}
              className="w-full accent-blue-500 h-1.5"
              data-testid="letter-spacing-slider"
            />
          </div>

          {/* Line Height */}
          <div>
            <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1 flex items-center justify-between">
              <span>Line Height</span>
              <span className="text-gray-400">{lineHeight.toFixed(1)}</span>
            </label>
            <input
              type="range"
              min={0.8}
              max={3.0}
              step={0.1}
              value={lineHeight}
              onChange={e => handleStyleChange('lineHeight', parseFloat(e.target.value))}
              className="w-full accent-blue-500 h-1.5"
              data-testid="line-height-slider"
            />
          </div>

          {/* Text Transform */}
          <div>
            <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1 flex items-center gap-1">
              <CaseSensitive className="w-2.5 h-2.5" /> Transform
            </label>
            <div className="flex gap-1">
              {TEXT_TRANSFORMS.map(t => (
                <button
                  key={t.value}
                  onClick={() => handleStyleChange('textTransform', t.value)}
                  className={`flex-1 py-1.5 rounded text-[10px] font-bold transition-all ${
                    textTransform === t.value
                      ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 ring-1 ring-blue-300'
                      : 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-100'
                  }`}
                  data-testid={`text-transform-${t.value}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Text Alignment */}
          <div>
            <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1 block">Align</label>
            <div className="flex gap-1">
              {[
                { value: 'left', icon: AlignLeft },
                { value: 'center', icon: AlignCenter },
                { value: 'right', icon: AlignRight },
              ].map(a => {
                const Icon = a.icon;
                return (
                  <button
                    key={a.value}
                    onClick={() => handleStyleChange('textAlign', a.value)}
                    className={`flex-1 flex items-center justify-center py-1.5 rounded transition-all ${
                      (currentStyle.textAlign || 'center') === a.value
                        ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600'
                        : 'bg-gray-50 dark:bg-gray-800 text-gray-400 hover:bg-gray-100'
                    }`}
                    data-testid={`align-${a.value}`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                  </button>
                );
              })}
            </div>
          </div>

          {/* Text Color */}
          <div>
            <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1 flex items-center gap-1">
              <Palette className="w-2.5 h-2.5" /> Color
            </label>
            <div className="flex gap-1.5 flex-wrap">
              {COLOR_PRESETS.map(c => (
                <button
                  key={c}
                  onClick={() => handleStyleChange('color', c)}
                  className={`w-5 h-5 rounded-full border-2 transition-all hover:scale-110 ${
                    currentStyle.color === c ? 'border-blue-500 scale-110' : 'border-gray-200 dark:border-gray-600'
                  }`}
                  style={{ background: c }}
                  data-testid={`color-${c.replace('#', '')}`}
                />
              ))}
            </div>
            <input
              type="color"
              value={currentStyle.color || '#000000'}
              onChange={e => handleStyleChange('color', e.target.value)}
              className="w-full h-6 rounded mt-1.5 cursor-pointer"
              data-testid="color-picker-custom"
            />
          </div>

          {/* Background Color — for text_note elements */}
          {element.type === 'text_note' && (
            <div>
              <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1 flex items-center gap-1">
                <Palette className="w-2.5 h-2.5" /> Background
              </label>
              <div className="flex gap-1.5 flex-wrap">
                <button
                  onClick={() => handleStyleChange('backgroundColor', 'transparent')}
                  className={`w-5 h-5 rounded-full border-2 transition-all hover:scale-110 ${
                    currentStyle.backgroundColor === 'transparent' ? 'border-blue-500 scale-110' : 'border-gray-200 dark:border-gray-600'
                  }`}
                  style={{ background: 'repeating-conic-gradient(#ccc 0% 25%, white 0% 50%) 50% / 8px 8px' }}
                  title="Transparent"
                  data-testid="bg-transparent"
                />
                {BG_COLOR_PRESETS.map(c => (
                  <button
                    key={c}
                    onClick={() => handleStyleChange('backgroundColor', c)}
                    className={`w-5 h-5 rounded-full border-2 transition-all hover:scale-110 ${
                      currentStyle.backgroundColor === c ? 'border-blue-500 scale-110' : 'border-gray-200 dark:border-gray-600'
                    }`}
                    style={{ background: c }}
                    data-testid={`bg-color-${c.replace('#', '')}`}
                  />
                ))}
              </div>
              <input
                type="color"
                value={currentStyle.backgroundColor || '#fdfbf8'}
                onChange={e => handleStyleChange('backgroundColor', e.target.value)}
                className="w-full h-6 rounded mt-1.5 cursor-pointer"
                data-testid="bg-color-picker-custom"
              />
            </div>
          )}

          {/* CTA Button — for hero title_block elements */}
          {element.type === 'title_block' && element.style === 'hero-title' && (
            <div className="pt-3 mt-3 border-t border-gray-100 dark:border-gray-800 space-y-2">
              <label className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider block">CTA Button</label>
              <div>
                <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1 block">Button Text</label>
                <input
                  type="text"
                  value={overrides?.buttonText ?? element.buttonText ?? ''}
                  onChange={e => onUpdate(sectionId, element.id, { buttonText: e.target.value })}
                  placeholder="Leave empty to hide"
                  className="w-full px-2.5 py-1.5 text-xs rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-800 dark:text-gray-200 outline-none focus:ring-1 focus:ring-blue-400"
                  data-testid="studio-cta-text"
                />
              </div>
              <div>
                <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1 block">Scroll Target</label>
                <input
                  type="text"
                  value={overrides?.buttonHref ?? element.buttonHref ?? ''}
                  onChange={e => onUpdate(sectionId, element.id, { buttonHref: e.target.value })}
                  placeholder="#rsvp or section ID"
                  className="w-full px-2.5 py-1.5 text-xs rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-800 dark:text-gray-200 outline-none focus:ring-1 focus:ring-blue-400"
                  data-testid="studio-cta-target"
                />
                <p className="text-[10px] text-gray-400 mt-1">Use # followed by the section ID to scroll</p>
              </div>
            </div>
          )}
        </div>
      </div>
    );
    return <>{textPanel}<EntrancePicker element={element} overrides={overrides} onUpdate={onUpdate} sectionId={sectionId} /></>;
  }

  // ── Image Style Panel ──
  if (isImage) {
    const borderRadius = parseInt(currentStyle.borderRadius) || 0;
    const opacity = currentStyle.opacity !== undefined ? currentStyle.opacity * 100 : 100;

    const imagePanel = (
      <div
        className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden"
        data-testid="element-style-panel"
      >
        <div className="px-3 py-2.5 border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50">
          <h3 className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
            <ImageIcon className="w-3 h-3" /> Image Style
          </h3>
        </div>

        <div className="p-3 space-y-3">
          {/* Aspect ratio */}
          <div>
            <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5 block">Aspect</label>
            <div className="flex gap-1 flex-wrap">
              {[
                { id: 'original', label: 'Original' },
                { id: '1/1', label: '1:1' },
                { id: '4/5', label: '4:5' },
                { id: '3/4', label: '3:4' },
                { id: '16/9', label: '16:9' },
              ].map(a => {
                const active = (currentStyle.aspectMode || 'original') === a.id;
                return (
                  <button
                    key={a.id}
                    onClick={() => handleStyleChange('aspectMode', a.id)}
                    className={`px-2 py-1 rounded text-[10px] font-medium transition-all ${
                      active
                        ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 ring-1 ring-blue-300'
                        : 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-100'
                    }`}
                    data-testid={`img-aspect-${a.id.replace('/', '-')}`}
                  >
                    {a.label}
                  </button>
                );
              })}
            </div>
            <div className="mt-1.5 text-[9px] text-gray-400">
              {(currentStyle.aspectMode || 'original') === 'original'
                ? "Uses the photo's natural shape"
                : 'Forces this ratio · the photo is cropped to fit'}
            </div>
          </div>

          {/* Quick width presets */}
          <div>
            <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5 block">Width</label>
            <div className="flex gap-1">
              {[
                { label: '25%', value: 0.25 },
                { label: '50%', value: 0.5 },
                { label: '75%', value: 0.75 },
                { label: 'Full', value: 1.0 },
              ].map(w => {
                // Merge override with base so the active preset reflects
                // the LATEST saved width, not the original template's. Section-v1
                // stores both legacy `w` (0-1) and `widthPercent` (0-100); read
                // percent first since the renderer prefers it.
                const _ov = overrides?.layout?.mobile || overrides?.layout?.desktop;
                const _ba = element.layout?.mobile || element.layout?.desktop;
                const currentW = (
                  _ov?.widthPercent != null ? _ov.widthPercent / 100
                  : _ov?.w != null ? _ov.w
                  : _ba?.widthPercent != null ? _ba.widthPercent / 100
                  : _ba?.w
                ) ?? 0.3;
                return (
                  <button
                    key={w.value}
                    onClick={() => onResize?.(sectionId, element.id, w.value)}
                    className={`flex-1 py-1.5 rounded text-[10px] font-medium transition-all ${
                      Math.abs(currentW - w.value) < 0.05
                        ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                        : 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-100'
                    }`}
                    data-testid={`img-width-${w.label.replace('%', '')}`}
                  >
                    {w.label}
                  </button>
                );
              })}
            </div>
            {/* Explicit edge-to-edge shortcut — same resize call but
                clearly communicates intent ("hug the canvas edges").
                Auto-centers via the resize handler's >=0.95 branch. */}
            <button
              type="button"
              onClick={() => onResize?.(sectionId, element.id, 1.0)}
              className="mt-1.5 w-full py-1.5 rounded text-[10.5px] font-medium bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-300 hover:bg-violet-100 dark:hover:bg-violet-900/40 transition-colors"
              data-testid="img-fit-to-mobile-width"
              title="Set width 100% + center on canvas"
            >
              Fit to mobile width
            </button>
          </div>

          {/* Border Radius */}
          <div>
            <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1 flex items-center justify-between">
              <span>Corners</span>
              <span className="text-gray-400">{borderRadius}px</span>
            </label>
            <input
              type="range"
              min={0}
              max={32}
              step={1}
              value={borderRadius}
              onChange={e => handleStyleChange('borderRadius', parseInt(e.target.value))}
              className="w-full accent-blue-500 h-1.5"
              data-testid="img-border-radius"
            />
          </div>

          {/* Opacity */}
          <div>
            <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1 flex items-center justify-between">
              <span>Opacity</span>
              <span className="text-gray-400">{Math.round(opacity)}%</span>
            </label>
            <input
              type="range"
              min={10}
              max={100}
              step={1}
              value={opacity}
              onChange={e => handleStyleChange('opacity', parseInt(e.target.value) / 100)}
              className="w-full accent-blue-500 h-1.5"
              data-testid="img-opacity"
            />
          </div>

          {/* Transparent toggle — for monograms / logos / ornaments with alpha */}
          <div className="flex items-center justify-between gap-2 pt-1">
            <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide leading-tight">
              Transparent image
              <span className="block normal-case text-[10px] text-gray-400 font-normal">No shadow, no cropping</span>
            </label>
            <button
              type="button"
              onClick={() => handleStyleChange('transparent', !currentStyle.transparent)}
              className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                currentStyle.transparent ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-700'
              }`}
              data-testid="img-transparent-toggle"
              aria-pressed={!!currentStyle.transparent}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  currentStyle.transparent ? 'translate-x-4' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
        </div>
      </div>
    );
    return <>{imagePanel}<EntrancePicker element={element} overrides={overrides} onUpdate={onUpdate} sectionId={sectionId} /></>;
  }

  // ── Shape Style Panel ──
  if (isShape) {
    const fill = currentStyle.fill || 'transparent';
    const stroke = currentStyle.stroke || '#000000';
    const strokeWidth = currentStyle.strokeWidth ?? 2;
    const opacity = currentStyle.opacity !== undefined ? currentStyle.opacity * 100 : 100;
    const borderRadius = currentStyle.borderRadius ?? 0;
    const isLine = element.style === 'horizontal_line';

    const shapePanel = (
      <div
        className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden"
        data-testid="element-style-panel"
      >
        <div className="px-3 py-2.5 border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50">
          <h3 className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Shape Style</h3>
        </div>

        <div className="p-3 space-y-3">
          {/* Quick width presets */}
          <div>
            <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5 block">Width</label>
            <div className="flex gap-1">
              {[
                { label: '25%', value: 0.25 },
                { label: '50%', value: 0.5 },
                { label: '75%', value: 0.75 },
                { label: 'Full', value: 1.0 },
              ].map(w => {
                // Merge override with base so the active preset reflects
                // the LATEST saved width, not the original template's. Section-v1
                // stores both legacy `w` (0-1) and `widthPercent` (0-100); read
                // percent first since the renderer prefers it.
                const _ov = overrides?.layout?.mobile || overrides?.layout?.desktop;
                const _ba = element.layout?.mobile || element.layout?.desktop;
                const currentW = (
                  _ov?.widthPercent != null ? _ov.widthPercent / 100
                  : _ov?.w != null ? _ov.w
                  : _ba?.widthPercent != null ? _ba.widthPercent / 100
                  : _ba?.w
                ) ?? 0.3;
                return (
                  <button
                    key={w.value}
                    onClick={() => onResize?.(sectionId, element.id, w.value)}
                    className={`flex-1 py-1.5 rounded text-[10px] font-medium transition-all ${
                      Math.abs(currentW - w.value) < 0.05
                        ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                        : 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-100'
                    }`}
                    data-testid={`shape-width-${w.label.replace('%', '')}`}
                  >
                    {w.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Height / Aspect Ratio — only for rectangle */}
          {element.style === 'rectangle' && (() => {
            const ar = currentStyle.aspectRatio || element.aspectRatio || '3/1';
            const [wPart, hPart] = ar.split('/').map(Number);
            const ratio = (hPart || 1) / (wPart || 3);
            const heightPct = Math.round(ratio * 100);
            return (
              <div>
                <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1 flex items-center justify-between">
                  <span>Height</span>
                  <span className="text-gray-400">{heightPct}%</span>
                </label>
                <input
                  type="range"
                  min={5}
                  max={200}
                  step={5}
                  value={heightPct}
                  onChange={e => {
                    const pct = parseInt(e.target.value);
                    const newAR = `100/${pct}`;
                    handleStyleChange('aspectRatio', newAR);
                  }}
                  className="w-full accent-blue-500 h-1.5"
                  data-testid="shape-height-slider"
                />
                <div className="flex justify-between text-[9px] text-gray-400 mt-0.5">
                  <span>Flat</span>
                  <span>Square</span>
                  <span>Tall</span>
                </div>
              </div>
            );
          })()}

          {/* Stroke / Line Color */}
          <div>
            <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1 block">
              {isLine ? 'Color' : 'Stroke'}
            </label>
            <div className="flex gap-1.5 flex-wrap">
              {COLOR_PRESETS.slice(0, 10).map(c => (
                <button
                  key={c}
                  onClick={() => handleStyleChange('stroke', c)}
                  className={`w-5 h-5 rounded-full border-2 transition-all hover:scale-110 ${
                    stroke === c ? 'border-blue-500 scale-110' : 'border-gray-200 dark:border-gray-600'
                  }`}
                  style={{ background: c }}
                  data-testid={`shape-stroke-${c.replace('#', '')}`}
                />
              ))}
            </div>
            <input type="color" value={stroke} onChange={e => handleStyleChange('stroke', e.target.value)}
              className="w-full h-5 rounded mt-1 cursor-pointer" data-testid="shape-stroke-picker" />
          </div>

          {/* Fill Color — not for lines */}
          {!isLine && (
            <div>
              <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1 block">Fill</label>
              <div className="flex gap-1.5 flex-wrap">
                <button
                  onClick={() => handleStyleChange('fill', 'transparent')}
                  className={`w-5 h-5 rounded-full border-2 transition-all hover:scale-110 ${
                    fill === 'transparent' ? 'border-blue-500 scale-110' : 'border-gray-200 dark:border-gray-600'
                  }`}
                  style={{ background: 'repeating-conic-gradient(#ddd 0% 25%, #fff 0% 50%) 50%/8px 8px' }}
                  title="Transparent"
                  data-testid="shape-fill-transparent"
                />
                {COLOR_PRESETS.slice(0, 8).map(c => (
                  <button
                    key={c}
                    onClick={() => handleStyleChange('fill', c)}
                    className={`w-5 h-5 rounded-full border-2 transition-all hover:scale-110 ${
                      fill === c ? 'border-blue-500 scale-110' : 'border-gray-200 dark:border-gray-600'
                    }`}
                    style={{ background: c }}
                    data-testid={`shape-fill-${c.replace('#', '')}`}
                  />
                ))}
              </div>
              <input type="color" value={fill === 'transparent' ? '#ffffff' : fill}
                onChange={e => handleStyleChange('fill', e.target.value)}
                className="w-full h-5 rounded mt-1 cursor-pointer" data-testid="shape-fill-picker" />
            </div>
          )}

          {/* Pattern Fill — rectangle only (MVP: stripes) */}
          {element.style === 'rectangle' && (() => {
            const fillStyle = currentStyle.fillStyle || 'solid';
            const patternType = currentStyle.patternType || 'stripes';
            const patternDirection = currentStyle.patternDirection || 'vertical';
            const baseColor = currentStyle.baseColor || '#f8f1e8';
            const stripeColor = currentStyle.stripeColor || '#23432f';
            const stripeWidth = currentStyle.stripeWidth ?? 12;
            const gapWidth = currentStyle.gapWidth ?? 12;
            const patternSizing = currentStyle.patternSizing || 'fixed';
            const stripeCycles = currentStyle.stripeCycles ?? 18;
            const stripeRatio = currentStyle.stripeRatio ?? 0.5;
            const stripeEdgeMode = currentStyle.stripeEdgeMode || 'even';
            const dotSize = currentStyle.dotSize ?? 6;
            const dotGap = currentStyle.dotGap ?? 14;
            const dotColumns = currentStyle.dotColumns ?? 8;
            const dotFitSize = currentStyle.dotFitSize ?? 50;
            const cellSize = currentStyle.cellSize ?? 16;
            const cellColumns = currentStyle.cellColumns ?? 8;
            const isPattern = fillStyle === 'pattern';
            const isFit = patternSizing === 'fit';
            const isStripes = patternType === 'stripes';
            const isDots = patternType === 'dots';
            const isChecker = patternType === 'checker';
            return (
              <div className="pt-1 border-t border-gray-100 dark:border-gray-800">
                <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5 block">Fill style</label>
                <div className="flex gap-1 mb-2">
                  {[
                    { v: 'solid', label: 'Solid' },
                    { v: 'pattern', label: 'Pattern' },
                  ].map(f => (
                    <button
                      key={f.v}
                      onClick={() => handleStyleChange('fillStyle', f.v)}
                      className={`flex-1 py-1.5 rounded text-[10px] font-medium transition-all ${
                        fillStyle === f.v
                          ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                          : 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-100'
                      }`}
                      data-testid={`shape-fillstyle-${f.v}`}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>

                {isPattern && (
                  <div className="space-y-2.5 pl-2 border-l-2 border-blue-200 dark:border-blue-900/40">
                    {/* Pattern type — Stripes / Dots / Checker. */}
                    <div>
                      <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1 block">Pattern</label>
                      <div className="flex gap-1">
                        {[
                          { v: 'stripes', label: 'Stripes' },
                          { v: 'dots', label: 'Dots' },
                          { v: 'checker', label: 'Checker' },
                        ].map(p => (
                          <button
                            key={p.v}
                            onClick={() => handleStyleChange('patternType', p.v)}
                            className={`flex-1 py-1.5 rounded text-[10px] font-medium transition-all ${
                              patternType === p.v
                                ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                                : 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-100'
                            }`}
                            data-testid={`shape-patterntype-${p.v}`}
                          >
                            {p.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Direction — stripes only (incl. diagonal variants) */}
                    {isStripes && (
                    <div>
                      <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1 block">Direction</label>
                      <div className="grid grid-cols-2 gap-1">
                        {[
                          { v: 'vertical', label: 'Vertical' },
                          { v: 'horizontal', label: 'Horizontal' },
                          { v: 'diagonal', label: 'Diagonal 45°' },
                          { v: 'diagonalReverse', label: 'Diagonal −45°' },
                        ].map(d => (
                          <button
                            key={d.v}
                            onClick={() => handleStyleChange('patternDirection', d.v)}
                            className={`py-1.5 rounded text-[10px] font-medium transition-all ${
                              patternDirection === d.v
                                ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                                : 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-100'
                            }`}
                            data-testid={`shape-pattern-direction-${d.v}`}
                          >
                            {d.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    )}

                    {/* Base color */}
                    <div>
                      <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1 block">Base color</label>
                      <div className="flex gap-1.5 flex-wrap">
                        {COLOR_PRESETS.slice(0, 8).map(c => (
                          <button
                            key={c}
                            onClick={() => handleStyleChange('baseColor', c)}
                            className={`w-5 h-5 rounded-full border-2 transition-all hover:scale-110 ${
                              baseColor === c ? 'border-blue-500 scale-110' : 'border-gray-200 dark:border-gray-600'
                            }`}
                            style={{ background: c }}
                            data-testid={`shape-pattern-base-${c.replace('#', '')}`}
                          />
                        ))}
                      </div>
                      <input type="color" value={baseColor}
                        onChange={e => handleStyleChange('baseColor', e.target.value)}
                        className="w-full h-5 rounded mt-1 cursor-pointer" data-testid="shape-pattern-base-picker" />
                    </div>

                    {/* Stripe color */}
                    <div>
                      <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1 block">Stripe color</label>
                      <div className="flex gap-1.5 flex-wrap">
                        {COLOR_PRESETS.slice(0, 8).map(c => (
                          <button
                            key={c}
                            onClick={() => handleStyleChange('stripeColor', c)}
                            className={`w-5 h-5 rounded-full border-2 transition-all hover:scale-110 ${
                              stripeColor === c ? 'border-blue-500 scale-110' : 'border-gray-200 dark:border-gray-600'
                            }`}
                            style={{ background: c }}
                            data-testid={`shape-pattern-stripe-${c.replace('#', '')}`}
                          />
                        ))}
                      </div>
                      <input type="color" value={stripeColor}
                        onChange={e => handleStyleChange('stripeColor', e.target.value)}
                        className="w-full h-5 rounded mt-1 cursor-pointer" data-testid="shape-pattern-stripe-picker" />
                    </div>

                    {/* ─── Stripes-only controls (sizing + fixed/fit + edge mode) ─── */}
                    {isStripes && <>

                    {/* Pattern sizing — Fixed (legacy px) vs Fit (responsive %).
                        Lives inside the stripes branch to preserve the exact
                        DOM/key order for existing stripe shapes. Dots and
                        checker render their own copy of this toggle below. */}
                    <div>
                      <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1 block">Pattern sizing</label>
                      <div className="flex gap-1">
                        {[
                          { v: 'fixed', label: 'Fixed px' },
                          { v: 'fit', label: 'Fit evenly' },
                        ].map(s => (
                          <button
                            key={s.v}
                            onClick={() => handleStyleChange('patternSizing', s.v)}
                            className={`flex-1 py-1.5 rounded text-[10px] font-medium transition-all ${
                              patternSizing === s.v
                                ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                                : 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-100'
                            }`}
                            data-testid={`shape-pattern-sizing-${s.v}`}
                          >
                            {s.label}
                          </button>
                        ))}
                      </div>
                      {isFit && (
                        <p className="text-[9px] text-gray-400 dark:text-gray-500 mt-1">
                          Lower count = wider stripes. Higher count = thinner stripes.
                        </p>
                      )}
                    </div>

                    {/* ─── FIXED MODE controls (existing): Stripe width / Gap width / Offset ─── */}
                    {!isFit && <>

                    {/* Stripe width */}
                    <div>
                      <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1 flex items-center justify-between">
                        <span>Stripe width</span>
                        <span className="text-gray-400">{stripeWidth}px</span>
                      </label>
                      <input
                        type="range"
                        min={1}
                        max={60}
                        step={1}
                        value={stripeWidth}
                        onChange={e => handleStyleChange('stripeWidth', parseInt(e.target.value))}
                        className="w-full accent-blue-500 h-1.5"
                        data-testid="shape-pattern-stripe-width"
                      />
                    </div>

                    {/* Gap width */}
                    <div>
                      <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1 flex items-center justify-between">
                        <span>Gap width</span>
                        <span className="text-gray-400">{gapWidth}px</span>
                      </label>
                      <input
                        type="range"
                        min={0}
                        max={60}
                        step={1}
                        value={gapWidth}
                        onChange={e => handleStyleChange('gapWidth', parseInt(e.target.value))}
                        className="w-full accent-blue-500 h-1.5"
                        data-testid="shape-pattern-gap-width"
                      />
                    </div>

                    {/* Pattern offset — shifts the repeated pattern inside
                        the rectangle without changing layout. One axis per
                        direction (the one parallel to stripe spacing). */}
                    {patternDirection === 'vertical' ? (() => {
                      const offsetX = currentStyle.patternOffsetX ?? 0;
                      return (
                        <div>
                          <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1 flex items-center justify-between">
                            <span>Horizontal offset</span>
                            <span className="text-gray-400">{offsetX}px</span>
                          </label>
                          <input
                            type="range"
                            min={-60}
                            max={60}
                            step={1}
                            value={offsetX}
                            onChange={e => handleStyleChange('patternOffsetX', parseInt(e.target.value))}
                            className="w-full accent-blue-500 h-1.5"
                            data-testid="shape-pattern-offset-x"
                          />
                        </div>
                      );
                    })() : (() => {
                      const offsetY = currentStyle.patternOffsetY ?? 0;
                      return (
                        <div>
                          <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1 flex items-center justify-between">
                            <span>Vertical offset</span>
                            <span className="text-gray-400">{offsetY}px</span>
                          </label>
                          <input
                            type="range"
                            min={-60}
                            max={60}
                            step={1}
                            value={offsetY}
                            onChange={e => handleStyleChange('patternOffsetY', parseInt(e.target.value))}
                            className="w-full accent-blue-500 h-1.5"
                            data-testid="shape-pattern-offset-y"
                          />
                        </div>
                      );
                    })()}
                    </>}

                    {/* ─── FIT MODE controls: Stripe count / Stripe balance ─── */}
                    {isFit && <>

                    <div>
                      <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1 flex items-center justify-between">
                        <span>Stripe count</span>
                        <span className="text-gray-400">{stripeCycles}</span>
                      </label>
                      <input
                        type="range"
                        min={2}
                        max={80}
                        step={1}
                        value={stripeCycles}
                        onChange={e => handleStyleChange('stripeCycles', parseInt(e.target.value))}
                        className="w-full accent-blue-500 h-1.5"
                        data-testid="shape-pattern-stripe-cycles"
                      />
                    </div>

                    <div>
                      <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1 flex items-center justify-between">
                        <span>Stripe balance</span>
                        <span className="text-gray-400">{Math.round(stripeRatio * 100)}%</span>
                      </label>
                      <input
                        type="range"
                        min={10}
                        max={90}
                        step={1}
                        value={Math.round(stripeRatio * 100)}
                        onChange={e => handleStyleChange('stripeRatio', parseInt(e.target.value) / 100)}
                        className="w-full accent-blue-500 h-1.5"
                        data-testid="shape-pattern-stripe-ratio"
                      />
                      <div className="flex justify-between text-[9px] text-gray-400 mt-0.5">
                        <span>Thin stripes</span>
                        <span>Thick stripes</span>
                      </div>
                    </div>

                    {/* Edge mode — controls whether the rectangle visually
                        ends on a gap (Even cycle, default) or always on a
                        stripe (Start/end with stripe). Pure rendering knob. */}
                    <div>
                      <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1 block">Edge mode</label>
                      <div className="flex gap-1">
                        {[
                          { v: 'even', label: 'Even cycle' },
                          { v: 'startEndStripe', label: 'Start/end stripe' },
                        ].map(m => (
                          <button
                            key={m.v}
                            onClick={() => handleStyleChange('stripeEdgeMode', m.v)}
                            className={`flex-1 py-1.5 rounded text-[10px] font-medium transition-all ${
                              stripeEdgeMode === m.v
                                ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                                : 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-100'
                            }`}
                            data-testid={`shape-pattern-edge-${m.v}`}
                          >
                            {m.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    </>}

                    </>}
                    {/* ─── Dots controls ─── */}
                    {isDots && <>
                    {/* Pattern sizing — same toggle as stripes. */}
                    <div>
                      <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1 block">Pattern sizing</label>
                      <div className="flex gap-1">
                        {[
                          { v: 'fixed', label: 'Fixed px' },
                          { v: 'fit', label: 'Fit evenly' },
                        ].map(s => (
                          <button
                            key={s.v}
                            onClick={() => handleStyleChange('patternSizing', s.v)}
                            className={`flex-1 py-1.5 rounded text-[10px] font-medium transition-all ${
                              patternSizing === s.v
                                ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                                : 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-100'
                            }`}
                            data-testid={`shape-pattern-sizing-${s.v}`}
                          >
                            {s.label}
                          </button>
                        ))}
                      </div>
                      {isFit && (
                        <p className="text-[9px] text-gray-400 dark:text-gray-500 mt-1">
                          Fit evenly keeps full dots inside — no half-dots at the edges.
                        </p>
                      )}
                    </div>

                    {/* FIXED dots (existing: dot size + dot gap) */}
                    {!isFit && <>
                    <div>
                      <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1 flex items-center justify-between">
                        <span>Dot size</span>
                        <span className="text-gray-400">{dotSize}px</span>
                      </label>
                      <input
                        type="range"
                        min={2}
                        max={40}
                        step={1}
                        value={dotSize}
                        onChange={e => handleStyleChange('dotSize', parseInt(e.target.value))}
                        className="w-full accent-blue-500 h-1.5"
                        data-testid="shape-pattern-dot-size"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1 flex items-center justify-between">
                        <span>Dot spacing</span>
                        <span className="text-gray-400">{dotGap}px</span>
                      </label>
                      <input
                        type="range"
                        min={2}
                        max={80}
                        step={1}
                        value={dotGap}
                        onChange={e => handleStyleChange('dotGap', parseInt(e.target.value))}
                        className="w-full accent-blue-500 h-1.5"
                        data-testid="shape-pattern-dot-gap"
                      />
                    </div>
                    </>}

                    {/* FIT dots: column count + dot size relative to tile */}
                    {isFit && <>
                    <div>
                      <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1 flex items-center justify-between">
                        <span>Dot columns</span>
                        <span className="text-gray-400">{dotColumns}</span>
                      </label>
                      <input
                        type="range"
                        min={2}
                        max={40}
                        step={1}
                        value={dotColumns}
                        onChange={e => handleStyleChange('dotColumns', parseInt(e.target.value))}
                        className="w-full accent-blue-500 h-1.5"
                        data-testid="shape-pattern-dot-columns"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1 flex items-center justify-between">
                        <span>Dot size</span>
                        <span className="text-gray-400">{dotFitSize}%</span>
                      </label>
                      <input
                        type="range"
                        min={10}
                        max={80}
                        step={1}
                        value={dotFitSize}
                        onChange={e => handleStyleChange('dotFitSize', parseInt(e.target.value))}
                        className="w-full accent-blue-500 h-1.5"
                        data-testid="shape-pattern-dot-fit-size"
                      />
                      <div className="flex justify-between text-[9px] text-gray-400 mt-0.5">
                        <span>Smaller</span>
                        <span>Larger</span>
                      </div>
                    </div>
                    </>}
                    </>}

                    {/* ─── Checker controls ─── */}
                    {isChecker && <>
                    <div>
                      <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1 block">Pattern sizing</label>
                      <div className="flex gap-1">
                        {[
                          { v: 'fixed', label: 'Fixed px' },
                          { v: 'fit', label: 'Fit evenly' },
                        ].map(s => (
                          <button
                            key={s.v}
                            onClick={() => handleStyleChange('patternSizing', s.v)}
                            className={`flex-1 py-1.5 rounded text-[10px] font-medium transition-all ${
                              patternSizing === s.v
                                ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                                : 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-100'
                            }`}
                            data-testid={`shape-pattern-sizing-${s.v}`}
                          >
                            {s.label}
                          </button>
                        ))}
                      </div>
                      {isFit && (
                        <p className="text-[9px] text-gray-400 dark:text-gray-500 mt-1">
                          Fit evenly divides into full cells — no partial cells at the edges.
                        </p>
                      )}
                    </div>

                    {/* FIXED checker (existing: cell size in px) */}
                    {!isFit && (
                    <div>
                      <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1 flex items-center justify-between">
                        <span>Cell size</span>
                        <span className="text-gray-400">{cellSize}px</span>
                      </label>
                      <input
                        type="range"
                        min={2}
                        max={60}
                        step={1}
                        value={cellSize}
                        onChange={e => handleStyleChange('cellSize', parseInt(e.target.value))}
                        className="w-full accent-blue-500 h-1.5"
                        data-testid="shape-pattern-cell-size"
                      />
                    </div>
                    )}

                    {/* FIT checker: column count drives full-cell rhythm */}
                    {isFit && (
                    <div>
                      <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1 flex items-center justify-between">
                        <span>Cell columns</span>
                        <span className="text-gray-400">{cellColumns}</span>
                      </label>
                      <input
                        type="range"
                        min={2}
                        max={40}
                        step={1}
                        value={cellColumns}
                        onChange={e => handleStyleChange('cellColumns', parseInt(e.target.value))}
                        className="w-full accent-blue-500 h-1.5"
                        data-testid="shape-pattern-cell-columns"
                      />
                    </div>
                    )}
                    </>}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Stroke Width / Thickness */}
          <div>
            <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1 flex items-center justify-between">
              <span>Thickness</span>
              <span className="text-gray-400">{strokeWidth}px</span>
            </label>
            <input
              type="range"
              min={1}
              max={isLine ? 20 : 10}
              step={1}
              value={strokeWidth}
              onChange={e => handleStyleChange('strokeWidth', parseInt(e.target.value))}
              className="w-full accent-blue-500 h-1.5"
              data-testid="shape-stroke-width"
            />
          </div>

          {/* Border Radius — only for rectangle */}
          {element.style === 'rectangle' && (
            <div>
              <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1 flex items-center justify-between">
                <span>Corners</span>
                <span className="text-gray-400">{borderRadius}px</span>
              </label>
              <input
                type="range"
                min={0}
                max={50}
                step={1}
                value={borderRadius}
                onChange={e => handleStyleChange('borderRadius', parseInt(e.target.value))}
                className="w-full accent-blue-500 h-1.5"
                data-testid="shape-border-radius"
              />
            </div>
          )}

          {/* Opacity */}
          <div>
            <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1 flex items-center justify-between">
              <span>Opacity</span>
              <span className="text-gray-400">{Math.round(opacity)}%</span>
            </label>
            <input
              type="range"
              min={10}
              max={100}
              step={1}
              value={opacity}
              onChange={e => handleStyleChange('opacity', parseInt(e.target.value) / 100)}
              className="w-full accent-blue-500 h-1.5"
              data-testid="shape-opacity"
            />
          </div>
        </div>
      </div>
    );
    return <>{shapePanel}<EntrancePicker element={element} overrides={overrides} onUpdate={onUpdate} sectionId={sectionId} /></>;
  }

  // ── Ornament Style Panel ──
  if (isOrnament) {
    const color = currentStyle.color || '#000000';
    const opacity = currentStyle.opacity !== undefined ? currentStyle.opacity * 100 : 100;

    const ornamentPanel = (
      <div
        className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden"
        data-testid="element-style-panel"
      >
        <div className="px-3 py-2.5 border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50">
          <h3 className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Ornament Style</h3>
        </div>

        <div className="p-3 space-y-3">
          {/* Quick width presets */}
          <div>
            <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5 block">Width</label>
            <div className="flex gap-1">
              {[
                { label: '25%', value: 0.25 },
                { label: '50%', value: 0.5 },
                { label: '75%', value: 0.75 },
                { label: 'Full', value: 1.0 },
              ].map(w => {
                // Merge override with base so the active preset reflects
                // the LATEST saved width, not the original template's. Section-v1
                // stores both legacy `w` (0-1) and `widthPercent` (0-100); read
                // percent first since the renderer prefers it.
                const _ov = overrides?.layout?.mobile || overrides?.layout?.desktop;
                const _ba = element.layout?.mobile || element.layout?.desktop;
                const currentW = (
                  _ov?.widthPercent != null ? _ov.widthPercent / 100
                  : _ov?.w != null ? _ov.w
                  : _ba?.widthPercent != null ? _ba.widthPercent / 100
                  : _ba?.w
                ) ?? 0.3;
                return (
                  <button
                    key={w.value}
                    onClick={() => onResize?.(sectionId, element.id, w.value)}
                    className={`flex-1 py-1.5 rounded text-[10px] font-medium transition-all ${
                      Math.abs(currentW - w.value) < 0.05
                        ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                        : 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-100'
                    }`}
                    data-testid={`orn-width-${w.label.replace('%', '')}`}
                  >
                    {w.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Color */}
          <div>
            <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1 block">Color</label>
            <div className="flex gap-1.5 flex-wrap">
              {COLOR_PRESETS.map(c => (
                <button
                  key={c}
                  onClick={() => handleStyleChange('color', c)}
                  className={`w-5 h-5 rounded-full border-2 transition-all hover:scale-110 ${
                    color === c ? 'border-blue-500 scale-110' : 'border-gray-200 dark:border-gray-600'
                  }`}
                  style={{ background: c }}
                  data-testid={`orn-color-${c.replace('#', '')}`}
                />
              ))}
            </div>
            <input type="color" value={color} onChange={e => handleStyleChange('color', e.target.value)}
              className="w-full h-6 rounded mt-1.5 cursor-pointer" data-testid="orn-color-picker" />
          </div>

          {/* Opacity */}
          <div>
            <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1 flex items-center justify-between">
              <span>Opacity</span>
              <span className="text-gray-400">{Math.round(opacity)}%</span>
            </label>
            <input
              type="range"
              min={10}
              max={100}
              step={1}
              value={opacity}
              onChange={e => handleStyleChange('opacity', parseInt(e.target.value) / 100)}
              className="w-full accent-blue-500 h-1.5"
              data-testid="orn-opacity"
            />
          </div>
        </div>
      </div>
    );
    return <>{ornamentPanel}<EntrancePicker element={element} overrides={overrides} onUpdate={onUpdate} sectionId={sectionId} /></>;
  }

  return null;
}


// ─── Vinyl Style Panel (with upload) ─────────────────────────────
function VinylStylePanel({ element, overrides, onUpdate, sectionId, eventId }) {
  const data = overrides || {};
  const musicUrl = data.musicUrl ?? element.musicUrl ?? '';
  const songTitle = data.songTitle ?? element.songTitle ?? 'Our Song';
  const artistName = data.artistName ?? element.artistName ?? 'Wedding Soundtrack';
  const coverImage = data.coverImage ?? element.coverImage ?? '';
  const playerStyle = data.playerStyle ?? element.playerStyle ?? 'vinyl';
  const vinylAccent = data.accentColor ?? element.accentColor ?? '';
  const vinylBg = data.bgColor ?? element.bgColor ?? '';

  const [uploading, setUploading] = useState(false);
  const [uploadingArt, setUploadingArt] = useState(false);
  const audioInputRef = useRef(null);
  const artInputRef = useRef(null);

  const token = localStorage.getItem('token');

  const handleAudioUpload = useCallback(async (file) => {
    if (!file || !eventId) return;
    const allowed = ['audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/aac', 'audio/mp4', 'audio/x-m4a'];
    if (!allowed.includes(file.type) && !file.name.match(/\.(mp3|wav|ogg|aac|m4a)$/i)) {
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/api/events/${eventId}/rsvp-page/upload-audio`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      if (res.data?.url || res.data?.audio_url) {
        onUpdate(sectionId, element.id, { musicUrl: res.data.url || res.data.audio_url });
      }
    } catch (err) {
      console.error('Audio upload failed:', err);
    } finally {
      setUploading(false);
    }
  }, [eventId, token, sectionId, element.id, onUpdate]);

  const handleArtUpload = useCallback(async (file) => {
    if (!file || !eventId) return;
    setUploadingArt(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('field', 'cover_art');
      const res = await axios.post(`${API}/api/events/${eventId}/rsvp-page/upload-image`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      if (res.data?.url || res.data?.image_url) {
        onUpdate(sectionId, element.id, { coverImage: res.data.image_url || res.data.url });
      }
    } catch (err) {
      console.error('Art upload failed:', err);
    } finally {
      setUploadingArt(false);
    }
  }, [eventId, token, sectionId, element.id, onUpdate]);

  const handleDrop = useCallback((e, type) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    if (file) {
      if (type === 'audio') handleAudioUpload(file);
      else handleArtUpload(file);
    }
  }, [handleAudioUpload, handleArtUpload]);

  return (
    <div className="space-y-3" data-testid="vinyl-style-panel">
      {/* Audio Upload */}
      <div>
        <label className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5 block">Upload Audio</label>
        {musicUrl ? (
          <div className="flex items-center gap-2 p-2 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
            <Music2 className="w-4 h-4 text-green-600 flex-shrink-0" />
            <span className="text-[10px] text-green-700 dark:text-green-400 truncate flex-1">{musicUrl.split('/').pop() || 'Audio loaded'}</span>
            <button
              onClick={() => onUpdate(sectionId, element.id, { musicUrl: '' })}
              className="p-0.5 rounded hover:bg-green-100 dark:hover:bg-green-800"
            >
              <X className="w-3 h-3 text-green-500" />
            </button>
          </div>
        ) : (
          <div
            onClick={() => audioInputRef.current?.click()}
            onDrop={e => handleDrop(e, 'audio')}
            onDragOver={e => e.preventDefault()}
            className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4 text-center cursor-pointer hover:border-blue-400 dark:hover:border-blue-500 hover:bg-blue-50/30 dark:hover:bg-blue-900/10 transition-all"
            data-testid="vinyl-audio-drop"
          >
            {uploading ? (
              <Loader2 className="w-5 h-5 mx-auto text-blue-500 animate-spin" />
            ) : (
              <>
                <Upload className="w-5 h-5 mx-auto text-gray-400 mb-1" />
                <p className="text-[10px] text-gray-500 dark:text-gray-400">Drop audio file or click</p>
                <p className="text-[9px] text-gray-400 mt-0.5">MP3, WAV, OGG, AAC, M4A (max 15MB)</p>
              </>
            )}
          </div>
        )}
        <input
          ref={audioInputRef}
          type="file"
          accept=".mp3,.wav,.ogg,.aac,.m4a,audio/*"
          className="hidden"
          onChange={e => { if (e.target.files?.[0]) handleAudioUpload(e.target.files[0]); }}
        />
        {/* Or paste URL */}
        <div className="mt-1.5">
          <input
            type="text"
            value={musicUrl}
            onChange={e => onUpdate(sectionId, element.id, { musicUrl: e.target.value })}
            placeholder="Or paste audio URL..."
            className="w-full px-2.5 py-1.5 text-xs rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-800 dark:text-gray-200 outline-none focus:ring-1 focus:ring-blue-400"
            data-testid="vinyl-music-url"
          />
        </div>
      </div>

      {/* Song Title */}
      <div>
        <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1 block">Song Title</label>
        <input
          type="text"
          value={songTitle}
          onChange={e => onUpdate(sectionId, element.id, { songTitle: e.target.value })}
          placeholder="Our Song"
          className="w-full px-2.5 py-1.5 text-xs rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-800 dark:text-gray-200 outline-none focus:ring-1 focus:ring-blue-400"
          data-testid="vinyl-song-title"
        />
      </div>

      {/* Artist */}
      <div>
        <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1 block">Artist Name</label>
        <input
          type="text"
          value={artistName}
          onChange={e => onUpdate(sectionId, element.id, { artistName: e.target.value })}
          placeholder="Artist name"
          className="w-full px-2.5 py-1.5 text-xs rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-800 dark:text-gray-200 outline-none focus:ring-1 focus:ring-blue-400"
          data-testid="vinyl-artist"
        />
      </div>

      {/* Album Art */}
      <div>
        <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5 block">Album Art</label>
        {coverImage ? (
          <div className="relative rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
            <img src={coverImage} alt="Album art" className="w-full h-20 object-cover" />
            <button
              onClick={() => onUpdate(sectionId, element.id, { coverImage: '' })}
              className="absolute top-1 right-1 p-1 rounded-full bg-black/50 hover:bg-black/70"
            >
              <X className="w-3 h-3 text-white" />
            </button>
          </div>
        ) : (
          <div
            onClick={() => artInputRef.current?.click()}
            onDrop={e => handleDrop(e, 'art')}
            onDragOver={e => e.preventDefault()}
            className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-3 text-center cursor-pointer hover:border-blue-400 transition-all"
            data-testid="vinyl-art-drop"
          >
            {uploadingArt ? (
              <Loader2 className="w-4 h-4 mx-auto text-blue-500 animate-spin" />
            ) : (
              <>
                <ImageIcon className="w-4 h-4 mx-auto text-gray-400 mb-1" />
                <p className="text-[10px] text-gray-400">Upload album art</p>
              </>
            )}
          </div>
        )}
        <input
          ref={artInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={e => { if (e.target.files?.[0]) handleArtUpload(e.target.files[0]); }}
        />
      </div>

      {/* Player Style */}
      <div>
        <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5 block">Player Style</label>
        <div className="flex gap-1.5">
          {[
            { id: 'vinyl', label: 'Vinyl Disc' },
            { id: 'minimal', label: 'Speaker Icon' },
          ].map(s => (
            <button
              key={s.id}
              onClick={() => onUpdate(sectionId, element.id, { playerStyle: s.id })}
              className={`flex-1 px-2.5 py-1.5 rounded-md text-[10px] font-medium transition-all ${
                playerStyle === s.id
                  ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 ring-1 ring-blue-300'
                  : 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-100'
              }`}
              data-testid={`vinyl-style-${s.id}`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tonearm Controls — only for vinyl style */}
      {playerStyle === 'vinyl' && (
        <div className="pt-2 mt-2 border-t border-gray-100 dark:border-gray-800 space-y-2">
          <label className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider block">Tonearm</label>
          {/* Size */}
          <div>
            <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 flex items-center justify-between mb-1">
              <span>Size</span>
              <span className="text-gray-400">{data.tonearmScale ?? 125}%</span>
            </label>
            <input
              type="range" min={50} max={150} step={5}
              value={data.tonearmScale ?? 125}
              onChange={e => onUpdate(sectionId, element.id, { tonearmScale: parseInt(e.target.value) })}
              className="w-full accent-blue-500 h-1.5"
              data-testid="tonearm-scale"
            />
          </div>
          {/* Horizontal position */}
          <div>
            <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 flex items-center justify-between mb-1">
              <span>Horizontal</span>
              <span className="text-gray-400">{data.tonearmX ?? 50}%</span>
            </label>
            <input
              type="range" min={0} max={100} step={2}
              value={data.tonearmX ?? 50}
              onChange={e => onUpdate(sectionId, element.id, { tonearmX: parseInt(e.target.value) })}
              className="w-full accent-blue-500 h-1.5"
              data-testid="tonearm-x"
            />
            <div className="flex justify-between text-[9px] text-gray-400 mt-0.5">
              <span>Left</span><span>Right</span>
            </div>
          </div>
          {/* Vertical position */}
          <div>
            <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 flex items-center justify-between mb-1">
              <span>Vertical</span>
              <span className="text-gray-400">{data.tonearmY ?? -12}%</span>
            </label>
            <input
              type="range" min={-20} max={40} step={2}
              value={data.tonearmY ?? -12}
              onChange={e => onUpdate(sectionId, element.id, { tonearmY: parseInt(e.target.value) })}
              className="w-full accent-blue-500 h-1.5"
              data-testid="tonearm-y"
            />
            <div className="flex justify-between text-[9px] text-gray-400 mt-0.5">
              <span>Up</span><span>Down</span>
            </div>
          </div>
          {/* Tonearm Color */}
          <div>
            <label className="text-[10px] font-medium text-gray-500 dark:text-gray-400 mb-1 block">Color</label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={data.tonearmColor || '#2b2c2e'}
                onChange={e => onUpdate(sectionId, element.id, { tonearmColor: e.target.value })}
                className="w-7 h-7 rounded cursor-pointer border border-gray-200 dark:border-gray-700 p-0.5"
                data-testid="tonearm-color"
              />
              <input
                type="text"
                value={data.tonearmColor || '#2b2c2e'}
                onChange={e => { if (/^#[0-9a-fA-F]{0,6}$/.test(e.target.value)) onUpdate(sectionId, element.id, { tonearmColor: e.target.value }); }}
                className="flex-1 px-2 py-1 text-[10px] font-mono rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none"
                data-testid="tonearm-color-hex"
              />
            </div>
          </div>
        </div>
      )}

      {/* Colors */}
      <div className="pt-2 mt-2 border-t border-gray-100 dark:border-gray-800 space-y-2">
        <label className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider block">Colors</label>
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={vinylAccent || '#2c241d'}
            onChange={e => onUpdate(sectionId, element.id, { accentColor: e.target.value })}
            className="w-7 h-7 rounded cursor-pointer border border-gray-200 dark:border-gray-700 p-0.5"
            data-testid="vinyl-accent-color"
          />
          <span className="text-xs text-gray-600 dark:text-gray-400">Accent</span>
          <input
            type="text"
            value={vinylAccent || '#2c241d'}
            onChange={e => { if (/^#[0-9a-fA-F]{0,6}$/.test(e.target.value)) onUpdate(sectionId, element.id, { accentColor: e.target.value }); }}
            className="flex-1 px-2 py-1 text-[10px] font-mono rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none"
            data-testid="vinyl-accent-hex"
          />
        </div>
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={vinylBg || '#f8f3eb'}
            onChange={e => onUpdate(sectionId, element.id, { bgColor: e.target.value })}
            className="w-7 h-7 rounded cursor-pointer border border-gray-200 dark:border-gray-700 p-0.5"
            data-testid="vinyl-bg-color"
          />
          <span className="text-xs text-gray-600 dark:text-gray-400">Background</span>
          <input
            type="text"
            value={vinylBg || '#f8f3eb'}
            onChange={e => { if (/^#[0-9a-fA-F]{0,6}$/.test(e.target.value)) onUpdate(sectionId, element.id, { bgColor: e.target.value }); }}
            className="flex-1 px-2 py-1 text-[10px] font-mono rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none"
            data-testid="vinyl-bg-hex"
          />
        </div>
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={data.discColor || '#000000'}
            onChange={e => onUpdate(sectionId, element.id, { discColor: e.target.value })}
            className="w-7 h-7 rounded cursor-pointer border border-gray-200 dark:border-gray-700 p-0.5"
            data-testid="vinyl-disc-color"
          />
          <span className="text-xs text-gray-600 dark:text-gray-400">Disc</span>
          <input
            type="text"
            value={data.discColor || '#000000'}
            onChange={e => { if (/^#[0-9a-fA-F]{0,6}$/.test(e.target.value)) onUpdate(sectionId, element.id, { discColor: e.target.value }); }}
            className="flex-1 px-2 py-1 text-[10px] font-mono rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none"
            data-testid="vinyl-disc-hex"
          />
        </div>
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={data.labelColor || '#efe5d6'}
            onChange={e => onUpdate(sectionId, element.id, { labelColor: e.target.value })}
            className="w-7 h-7 rounded cursor-pointer border border-gray-200 dark:border-gray-700 p-0.5"
            data-testid="vinyl-label-color"
          />
          <span className="text-xs text-gray-600 dark:text-gray-400">Label</span>
          <input
            type="text"
            value={data.labelColor || '#efe5d6'}
            onChange={e => { if (/^#[0-9a-fA-F]{0,6}$/.test(e.target.value)) onUpdate(sectionId, element.id, { labelColor: e.target.value }); }}
            className="flex-1 px-2 py-1 text-[10px] font-mono rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none"
            data-testid="vinyl-label-hex"
          />
        </div>
      </div>
    </div>
  );
}


// ─── Slideshow Element Style Panel ────────────────────────────
function SlideshowStylePanel({ element, overrides, onUpdate, sectionId, eventId }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const images = overrides?.images || element.images || [];
  const transition = overrides?.transition || element.transition || 'slide';
  const speed = overrides?.speed || element.speed || 'medium';
  const aspectRatio = overrides?.aspectRatio || element.aspectRatio || '16/9';

  const handleImageUpload = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(
        `${API}/api/events/${eventId}/rsvp-page/upload-image`,
        formData,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      const url = res.data.image_url || res.data.url;
      onUpdate(sectionId, element.id, { images: [...images, url] });
    } catch {
      // silent fail
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }, [eventId, sectionId, element.id, images, onUpdate]);

  const handleRemoveImage = useCallback((idx) => {
    const next = images.filter((_, i) => i !== idx);
    onUpdate(sectionId, element.id, { images: next });
  }, [images, sectionId, element.id, onUpdate]);

  return (
    <div
      className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden"
      data-testid="slideshow-element-style-panel"
    >
      <div className="px-3 py-2.5 border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50">
        <h3 className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
          <ImageIcon className="w-3 h-3" /> Slideshow
        </h3>
      </div>

      <div className="p-3 space-y-4">
        {/* Images */}
        <div>
          <label className="text-[10px] font-medium text-gray-500 uppercase tracking-wider mb-2 block">Images</label>
          <div className="flex flex-wrap gap-2 mb-2">
            {images.map((img, i) => (
              <div key={i} className="relative w-16 h-12 rounded overflow-hidden group">
                <img
                  src={img.startsWith('/') ? `${API}${img}` : img}
                  alt=""
                  className="w-full h-full object-cover"
                />
                <button
                  onClick={() => handleRemoveImage(i)}
                  className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`slideshow-remove-image-${i}`}
                >
                  <X className="w-2.5 h-2.5" />
                </button>
              </div>
            ))}
          </div>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg border border-dashed border-gray-300 dark:border-gray-700 text-xs text-gray-500 hover:border-blue-400 hover:text-blue-500 transition-colors"
            data-testid="slideshow-add-image-btn"
          >
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            {uploading ? 'Uploading...' : 'Add image'}
          </button>
        </div>

        {/* Transition */}
        <div>
          <label className="text-[10px] font-medium text-gray-500 uppercase tracking-wider mb-1.5 block">Transition</label>
          <div className="flex gap-1.5">
            {[{ v: 'slide', l: 'Slide' }, { v: 'crossfade', l: 'Crossfade' }, { v: 'zoom', l: 'Zoom In' }].map(t => (
              <button
                key={t.v}
                onClick={() => onUpdate(sectionId, element.id, { transition: t.v })}
                className={`flex-1 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  transition === t.v
                    ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-500 hover:bg-gray-200'
                }`}
                data-testid={`slideshow-transition-${t.v}`}
              >
                {t.l}
              </button>
            ))}
          </div>
        </div>

        {/* Speed */}
        <div>
          <label className="text-[10px] font-medium text-gray-500 uppercase tracking-wider mb-1.5 block">Speed</label>
          <div className="flex gap-1.5">
            {['slow', 'medium', 'fast'].map(s => (
              <button
                key={s}
                onClick={() => onUpdate(sectionId, element.id, { speed: s })}
                className={`flex-1 py-1.5 rounded-lg text-xs font-medium capitalize transition-colors ${
                  speed === s
                    ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-500 hover:bg-gray-200'
                }`}
                data-testid={`slideshow-speed-${s}`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {/* Aspect Ratio */}
        <div>
          <label className="text-[10px] font-medium text-gray-500 uppercase tracking-wider mb-1.5 block">Aspect Ratio</label>
          <div className="flex gap-1.5">
            {[{ v: '16/9', l: '16:9' }, { v: '4/3', l: '4:3' }, { v: '1/1', l: '1:1' }, { v: '3/4', l: '3:4' }].map(a => (
              <button
                key={a.v}
                onClick={() => onUpdate(sectionId, element.id, { aspectRatio: a.v })}
                className={`flex-1 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  aspectRatio === a.v
                    ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-500 hover:bg-gray-200'
                }`}
                data-testid={`slideshow-aspect-${a.l}`}
              >
                {a.l}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}


// ─── Entrance Animation Picker ────────────────────────────────
const ENTRANCE_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'fade', label: 'Fade' },
  { value: 'slide-up', label: 'Slide Up' },
  { value: 'slide-down', label: 'Slide Down' },
  { value: 'slide-left', label: 'Slide Left' },
  { value: 'slide-right', label: 'Slide Right' },
  { value: 'zoom', label: 'Zoom' },
  { value: 'typewriter', label: 'Typewriter' },
  { value: 'word-reveal', label: 'Word Reveal' },
];

const DELAY_OPTIONS = [
  { value: 0, label: '0s' },
  { value: 0.1, label: '0.1s' },
  { value: 0.2, label: '0.2s' },
  { value: 0.3, label: '0.3s' },
  { value: 0.5, label: '0.5s' },
];

function EntrancePicker({ element, overrides, onUpdate, sectionId }) {
  const entrance = overrides?.entrance || element.entrance || 'none';
  const entranceDelay = overrides?.entranceDelay ?? element.entranceDelay ?? 0;

  return (
    <div
      className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden mt-2"
      data-testid="entrance-picker-panel"
    >
      <div className="px-3 py-2.5 border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50">
        <h3 className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
          Scroll Entrance
        </h3>
      </div>

      <div className="p-3 space-y-3">
        {/* Animation type */}
        <div>
          <label className="text-[10px] font-medium text-gray-500 uppercase tracking-wider mb-1.5 block">Animation</label>
          <div className="flex flex-wrap gap-1">
            {ENTRANCE_OPTIONS.map(o => (
              <button
                key={o.value}
                onClick={() => onUpdate(sectionId, element.id, { entrance: o.value })}
                className={`px-2.5 py-1.5 rounded-lg text-[10px] font-medium transition-colors ${
                  entrance === o.value
                    ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-500 hover:bg-gray-200'
                }`}
                data-testid={`entrance-${o.value}`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>

        {/* Delay */}
        {entrance !== 'none' && (
          <div>
            <label className="text-[10px] font-medium text-gray-500 uppercase tracking-wider mb-1.5 block">Delay</label>
            <div className="flex gap-1">
              {DELAY_OPTIONS.map(d => (
                <button
                  key={d.value}
                  onClick={() => onUpdate(sectionId, element.id, { entranceDelay: d.value })}
                  className={`flex-1 py-1.5 rounded-lg text-[10px] font-medium transition-colors ${
                    entranceDelay === d.value
                      ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-500 hover:bg-gray-200'
                  }`}
                  data-testid={`entrance-delay-${d.value}`}
                >
                  {d.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
